// Live-watch coordinator: keeps WATCH subscriptions and unseen-row counters
// for query tabs alive across page navigations. The store outlives any
// single React component, which is what lets the sidebar badge keep ticking
// while the user is on the Home page.
//
// Counting model:
//   - Per tab badge: ticks once per server notify on this tab's table.
//     Cleared when the user focuses that specific tab (markRead).
//   - Sidebar badge: counts *unique events* per table. If three tabs watch
//     the same table and one INSERT happens, every tab badge ticks +1 (each
//     tab missed one change), but the sidebar only ticks +1 (one underlying
//     event). Focusing any tab on that table marks the table seen up to its
//     current event count, dropping the sidebar contribution to 0.
//
// Socket model:
//   - One WATCH per (table) per WS connection, regardless of tab count.
//     The store fans the single notify out to every tab's onChange callback.
//
// Usage:
//   import * as live from '../data/live-store.js';
//   await live.enable(tabId, sql, table, () => rerunForUI(tabId));
//   live.markRead(tabId);   // when tab becomes the focused one
//   await live.disable(tabId);
//   live.subscribe(() => forceUpdate());

import { getSocket, onSocketOpen } from './socket.js';

/** @type {Map<string|number, { sql: string, table: string, onChange: () => void }>} */
const subs = new Map();
/** @type {Map<string|number, number>} */
const unseen = new Map();
/** @type {Map<string, { socketHandle: { cancel: () => Promise<void> }|null, tabs: Set<string|number>, eventCount: number }>} */
const tableState = new Map();
/** @type {Map<string, number>} */
const tableSeen = new Map();
/** @type {Set<() => void>} */
const listeners = new Set();

function broadcast() {
  for (const cb of listeners) { try { cb(); } catch { /* listener error */ } }
}

// Re-register one WATCH per table when the socket reconnects.
onSocketOpen(async (s) => {
  for (const [table, st] of tableState.entries()) {
    if (st.tabs.size === 0) continue;
    try {
      st.socketHandle = await s.watch(table, (n) => onTableNotify(table, n));
    } catch { /* will retry on next reconnect */ }
  }
});

function onTableNotify(table, n) {
  const st = tableState.get(table);
  if (!st || st.tabs.size === 0) return;
  const changed = typeof n.changed === 'number' ? n.changed : 1;

  // Each tab's onChange re-runs the SELECT and resolves to a boolean: true
  // (or undefined) = "this tab's view changed, please tick", false = "the
  // re-run produced the same rows, don't tick". The sidebar's eventCount
  // ticks once per notify but only if AT LEAST ONE tab confirmed a real
  // change — otherwise the underlying event was irrelevant to every live
  // view of this table and shouldn't show up in the chrome at all.
  let counted = false;
  for (const tabId of [...st.tabs]) {
    const entry = subs.get(tabId);
    if (!entry) continue;
    Promise.resolve(entry.onChange())
      .then((viewChanged) => {
        if (viewChanged === false) return;
        unseen.set(tabId, (unseen.get(tabId) || 0) + changed);
        if (!counted) {
          counted = true;
          st.eventCount += changed;
        }
        broadcast();
      })
      .catch(() => { /* re-run error: don't tick, error surfaces in runState */ });
  }
}

export async function enable(tabId, sql, table, onChange) {
  await disable(tabId);
  const s = await getSocket();
  let st = tableState.get(table);
  if (!st) {
    // First tab on this table → open one WATCH at the socket level.
    const handle = await s.watch(table, (n) => onTableNotify(table, n));
    st = { socketHandle: handle, tabs: new Set(), eventCount: 0 };
    tableState.set(table, st);
  }
  st.tabs.add(tabId);
  subs.set(tabId, { sql, table, onChange });
  // Setting initial unseen=0 makes downstream `getUnseen` cheap and avoids
  // a stale value from a prior live session leaking through.
  unseen.set(tabId, 0);
  broadcast();
}

export async function disable(tabId) {
  const e = subs.get(tabId);
  subs.delete(tabId);
  unseen.delete(tabId);
  if (e) {
    const st = tableState.get(e.table);
    if (st) {
      st.tabs.delete(tabId);
      // Last tab on this table → tear down the WATCH on the wire too.
      if (st.tabs.size === 0) {
        if (st.socketHandle) {
          try { await st.socketHandle.cancel(); } catch { /* socket may be closing */ }
        }
        tableState.delete(e.table);
        tableSeen.delete(e.table);
      }
    }
  }
  broadcast();
}

export function isLive(tabId) { return subs.has(tabId); }
export function getUnseen(tabId) { return unseen.get(tabId) || 0; }

export function totalUnseen() {
  // Sum of (events - seen) across all watched tables. Deduplicated: one
  // INSERT into a table watched by N tabs contributes 1, not N.
  let t = 0;
  for (const [table, st] of tableState) {
    t += Math.max(0, st.eventCount - (tableSeen.get(table) || 0));
  }
  return t;
}

export function markRead(tabId) {
  // Per-tab clear (drives the tab's own badge).
  if ((unseen.get(tabId) || 0) !== 0) unseen.set(tabId, 0);
  // Mark this tab's table as seen up to the current event count (drives
  // the sidebar badge). Other tabs on the same table keep their per-tab
  // unseen, but the sidebar drops because the user has now visited that
  // table at least once.
  const e = subs.get(tabId);
  if (e) {
    const st = tableState.get(e.table);
    if (st) tableSeen.set(e.table, st.eventCount);
  }
  broadcast();
}

// Drop subscriptions for tabs that no longer exist in the UI.
export function pruneTo(activeTabIds) {
  const set = new Set(activeTabIds);
  for (const id of [...subs.keys()]) {
    if (!set.has(id)) disable(id); // fire-and-forget
  }
}

export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
